import { useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
import EventPage from "./EventPage";
import {
  getEventBySlug,
  EventDetail,
  setEventTestPricing,
} from "../../api/events";
import { getRegistrationCatalog, invalidateRegistrationCatalogCache } from "../../api/registration";
import { isSuperAdminRole } from "../../api/admin";
import { GlobalContext } from "../../context/globalContext";
import { getEventImage } from "./eventImages";
import map from "../../assets/media/background/map.png";
import LoadingPanel from "../../components/LoadingPanel/LoadingPanel";
import ErrorAlert from "../../components/ErrorAlert/ErrorAlert";
import { getUserFacingErrorMessage } from "../../utils/userFacingError";
import {
  deriveRegistrationStatusLabel,
  formatEventDateRange,
  formatEventTimeRange,
} from "../../utils/eventDates";
import { resolveRegistrationMode } from "../../utils/registrationMode";

function buildLocation(event: EventDetail): string {
  const parts = [event.venueName, event.venueAddress, event.city, event.country]
    .map((p) => (p ?? "").trim())
    .filter(Boolean);
  return parts.join(", ");
}

function deriveStatus(event: EventDetail): string {
  return deriveRegistrationStatusLabel(event);
}

function splitDescription(description: string): string[] {
  return description
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
}

function parseTags(tags: string[] | null | undefined): string[] {
  if (!tags || !Array.isArray(tags)) return [];
  return tags.filter((tag) => typeof tag === "string" && tag.trim());
}

export default function EventPageDynamic() {
  const { slug } = useParams<{ slug: string }>();
  const { currentUser } = useContext(GlobalContext);
  const [event, setEvent] = useState<EventDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [pricingRefreshKey, setPricingRefreshKey] = useState(0);
  const [isTogglingTestPricing, setIsTogglingTestPricing] = useState(false);
  const [testPricingError, setTestPricingError] = useState<string | null>(null);

  const isSuperAdmin = isSuperAdminRole(currentUser?.role);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    // Warm the registration-catalog cache while event detail loads so rates
    // paint without a second network round-trip after the page mounts.
    void getRegistrationCatalog(slug).catch(() => {
      /* rates section will retry / show its own error */
    });
    getEventBySlug(slug)
      .then((data) => {
        if (cancelled) return;
        setEvent(data);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(err);
        setError(
          getUserFacingErrorMessage(err, "Failed to load event."),
        );
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  async function handleTestPricingToggle() {
    if (!event) return;
    setIsTogglingTestPricing(true);
    setTestPricingError(null);
    try {
      const updated = await setEventTestPricing(
        event.id,
        !event.testPricingEnabled,
      );
      setEvent((prev) =>
        prev ? { ...prev, testPricingEnabled: updated.testPricingEnabled } : prev,
      );
      invalidateRegistrationCatalogCache(event.slug);
      setPricingRefreshKey((k) => k + 1);
    } catch (err) {
      setTestPricingError(
        getUserFacingErrorMessage(err, "Failed to update test pricing."),
      );
    } finally {
      setIsTogglingTestPricing(false);
    }
  }

  if (isLoading) {
    return (
      <LoadingPanel
        title="Loading event details"
        subtitle="Retrieving conference information and registration options."
      />
    );
  }

  if (error || !event) {
    const isNotFound = error === "Event not found";
    return (
      <div className="max-w-[920px] mx-auto py-24 text-center px-6">
        <h1 className="text-2xl font-bold text-slate-900 mb-3">
          {isNotFound ? "Event not found" : "Unable to load event"}
        </h1>
        <p className="text-slate-600">
          {isNotFound
            ? "The event you are looking for does not exist or is no longer available."
            : (error ??
              "We couldn't load this event. Please try again later.")}
        </p>
      </div>
    );
  }

  const img = getEventImage(event.slug, event.coverImageUrl);
  const longParagraphs = splitDescription(event.description);
  const shortParagraphs = event.excerpt
    ? [event.excerpt]
    : longParagraphs.slice(0, 1);

  return (
    <>
      <Helmet>
        <title>{event.title} | Events House</title>
      </Helmet>
      {isSuperAdmin && (
        <div className="max-w-[1100px] mx-auto px-6 mb-4">
          <div className="flex flex-wrap items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded-sm px-4 py-3 text-sm">
            <div>
              <p className="font-semibold text-amber-900">Super admin</p>
              <p className="text-amber-800">
                Test pricing is{" "}
                <span className="font-medium">
                  {event.testPricingEnabled ? "enabled" : "disabled"}
                </span>{" "}
                for this event.
              </p>
              {testPricingError && (
                <ErrorAlert className="mt-1">{testPricingError}</ErrorAlert>
              )}
            </div>
            <button
              type="button"
              className="eh-btn-secondary text-xs px-4 py-2"
              disabled={isTogglingTestPricing}
              onClick={handleTestPricingToggle}
            >
              {isTogglingTestPricing
                ? "Updating…"
                : event.testPricingEnabled
                  ? "Disable test pricing"
                  : "Enable test pricing"}
            </button>
          </div>
        </div>
      )}
      <EventPage
        eventSlug={event.slug}
        registrationMode={resolveRegistrationMode(
          event.slug,
          event.registrationMode,
        )}
        eventName={event.title}
        tags={parseTags(event.tags)}
        img={img}
        ticketTypes={event.ticketTypes ?? []}
        pricingRefreshKey={pricingRefreshKey}
        basicInfo={{
          eventDescriptionShort: shortParagraphs,
          eventDescriptionLong: longParagraphs,
          date: formatEventDateRange(event.startDate, event.endDate),
          time: formatEventTimeRange(event.startDate, event.endDate),
          location: buildLocation(event),
          status: deriveStatus(event),
          link: event.websiteUrl ?? undefined,
          email: event.contactEmail ?? undefined,
        }}
        contactInfo={{
          email: event.contactEmail ?? "contact@eventshouse.net",
          office: buildLocation(event),
          mapImg: map,
        }}
      />
    </>
  );
}
